export interface PotreeSourceUrls {
  metadataUrl: string;
  hierarchyUrl: string;
  octreeUrl: string;
}

export async function loadModelPotree(
  id: string,
  source: PotreeSourceUrls,
  options?: {
    frameCamera?: boolean;
  },
): Promise<string | undefined> {
  const viewerStore = useViewerStore();
  const { $math } = useNuxtApp();

  const loader = await viewerStore.ensurePotreeLoaderPlugin();
  const viewer = viewerStore.getHelpers().viewer;

  if (!viewerStore.isInitialized || !loader || !viewer) return;

  const { PotreeLoaderPlugin } = await import('@xeovision/xeokit-potree-plugin');
  const t0 = performance.now();

  const model = loader.load(
    new PotreeLoaderPlugin.V2Files(source.metadataUrl, source.hierarchyUrl, source.octreeUrl),
  );

  return new Promise((resolve, reject) => {
    model.on('loaded', () => {
      console.log(`Model ${id} loaded in ${Math.floor(performance.now() - t0) / 1000.0} seconds`);

      viewerStore.registerPotreeModel(id, model);
      void applyCurrentMaterial();
      if (options?.frameCamera ?? true) frameCameraOnAABB(model.aabb);
      resolve(id);
    });

    model.on('error', (error) => {
      console.error(error);
      reject(error);
    });
  });

  async function applyCurrentMaterial() {
    const material = await viewerStore.getPotreeMaterial();
    if (material) model.setSurfaceMaterial(material);
  }

  function frameCameraOnAABB(aabb: number[]) {
    const camera = viewer!.camera;
    const diag = $math.getAABB3Diag(aabb);
    const look = $math.getAABB3Center(aabb);
    const offset = (diag * 1.4) / Math.sqrt(3);

    camera.look = look;
    camera.eye = [look[0] + offset, look[1] + offset, look[2] + offset];
    camera.perspective.far = Math.max(camera.perspective.far, 20000);
    camera.ortho.far = Math.max(camera.ortho.far, 20000);
    camera.ortho.scale = diag;
  }
}
